import { useEffect, useState } from 'react'
import { Plus, Pencil } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { fmtEuro } from '../utils/fmt'

const emptyForm = { designacao: '', permilagem: '', area: '', andar: '', tipo: '', estado: 'OCUPADO', valorQuota: '' }

export default function Fracoes() {
  const { condominioAtivo } = useAppStore()
  const [fracoes, setFracoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')

  const load = () => {
    if (!condominioAtivo) return
    setLoading(true)
    api.get('/fracoes', { params: { condominioId: condominioAtivo.id } })
      .then(r => setFracoes(r.data)).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [condominioAtivo])

  const openCreate = () => { setForm(emptyForm); setEditId(null); setErro(''); setModal(true) }
  const openEdit = (f) => {
    setForm({ designacao: f.designacao, permilagem: f.permilagem ?? '', area: f.area ?? '', andar: f.andar ?? '', tipo: f.tipo ?? '', estado: f.estado, valorQuota: f.valorQuota ?? '' })
    setEditId(f.id); setErro(''); setModal(true)
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      const payload = {
        designacao: form.designacao,
        permilagem: parseFloat(form.permilagem) || 0,
        area: form.area ? parseFloat(form.area) : null,
        andar: form.andar || null,
        tipo: form.tipo || null,
        estado: form.estado,
        valorQuota: parseFloat(form.valorQuota) || 0
      }
      if (editId) await api.put(`/fracoes/${editId}`, payload)
      else await api.post('/fracoes', { ...payload, condominioId: condominioAtivo.id })
      setModal(false); load()
    } catch (err) { setErro(err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || 'Erro.') }
    finally { setSaving(false) }
  }

  const totalPermilagem = fracoes.reduce((s, f) => s + (f.permilagem || 0), 0)
  const totalQuotas = fracoes.reduce((s, f) => s + (f.valorQuota || 0), 0)

  return (
    <>
      <Topbar title="Frações" actions={
        <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={13} /> Nova fração</button>
      } />
      <div className="page-content">
        <div className="page-header">
          <h1>Frações — {condominioAtivo?.nome}</h1>
          <p>
            {fracoes.length} frações · Permilagem total: {totalPermilagem.toFixed(1)}‰ · Total quotas/mês: {fmtEuro(totalQuotas)}
            {Math.abs(totalPermilagem - 1000) > 1 && totalPermilagem > 0 && <span style={{ color: 'var(--amber-dark)', marginLeft: 8 }}>⚠ permilagem deve somar 1000‰</span>}
          </p>
        </div>

        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          Para lançar quotas mensais vai a <strong>Financeiro → Quotas</strong>.
        </div>

        <div className="card">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Designação</th><th>Quota/mês</th><th>Permilagem</th><th>Tipo</th><th>Andar</th><th>Área</th><th>Condómino</th><th>Estado</th><th></th></tr>
                </thead>
                <tbody>
                  {fracoes.length === 0 ? (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>
                      Sem frações. Cria a primeira fração.
                    </td></tr>
                  ) : fracoes.map(f => (
                    <tr key={f.id}>
                      <td><strong>{f.designacao}</strong></td>
                      <td>
                        {f.valorQuota > 0
                          ? <span style={{ fontWeight: 600, color: 'var(--green-dark)' }}>{fmtEuro(f.valorQuota)}</span>
                          : <span className="badge badge-amber">Não definido</span>}
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>{f.permilagem}‰</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{f.tipo || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{f.andar || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{f.area ? `${f.area} m²` : '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {f.condominosAssociados?.filter(cf => cf.ativo).map(cf => cf.condomino.nome).join(', ') || '—'}
                      </td>
                      <td>
                        <span className={`badge badge-${f.estado === 'OCUPADO' ? 'green' : f.estado === 'VAGO' ? 'amber' : 'red'}`}>
                          {f.estado}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm" onClick={() => openEdit(f)} title="Editar"><Pencil size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Editar fração' : 'Nova fração'}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Designação * (ex: 1A)</label>
                  <input className="form-input" value={form.designacao} onChange={e => setForm({ ...form, designacao: e.target.value.toUpperCase() })} required disabled={!!editId} />
                </div>
                <div className="form-group">
                  <label className="form-label">Quota mensal (€)</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={form.valorQuota} onChange={e => setForm({ ...form, valorQuota: e.target.value })} placeholder="0.00" />
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>Valor fixo da quota mensal</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Permilagem (‰)</label>
                  <input className="form-input" type="number" step="0.1" min="0" max="1000" value={form.permilagem} onChange={e => setForm({ ...form, permilagem: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Área (m²)</label>
                  <input className="form-input" type="number" step="0.1" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <input className="form-input" placeholder="T2, T3, Loja..." value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Andar</label>
                  <input className="form-input" placeholder="1º, R/C..." value={form.andar} onChange={e => setForm({ ...form, andar: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })}>
                  <option value="OCUPADO">Ocupado</option>
                  <option value="VAGO">Vago</option>
                  <option value="OBRAS">Em obras</option>
                </select>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" /> : editId ? 'Guardar' : 'Criar fração'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
